import { computed, inject, Injectable, resource } from '@angular/core';
import { environment } from 'src/environments/environment';
import { APIService } from './api.service';
import { firstValueFrom, from } from 'rxjs';
import {
  defaultTolBez,
  MessungDropdownList,
  VerknüpfungsTyp,
  ZählerMode,
} from 'src/app/content/_components/_overlays/zaehlerverwaltung/zaehler.enums';
import {
  ZählerName,
  ZählerWert,
  ToleranzenBezeichnungen,
  ZählerEinheit,
  ZählerPrüfung,
  MesswertErfassung,
  MesswertEintrag,
  MessungModel,
  ZaehlerObjektInfo,
} from 'src/app/content/_components/_overlays/zaehlerverwaltung/zaehler.types';
import { MRproResponseInfo, ID, MRproResponse } from 'src/app/types/api';
import { Table } from 'src/app/models/ui/table';
import { HttpClient, httpResource } from '@angular/common/http';
import { ZaehlerDB } from '../_offline/zaehlerDB';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { SkizzenPoint } from 'src/app/content/_components/_overlays/zaehlerverwaltung/edit-skizzen-points/edit-skizzen-points';
import { PrüfungsTypStore } from '../Shared/prüfung-typ.store';

const ROUTE = `${environment.apiUrl}/zaehler`;

@Injectable({
  providedIn: 'root',
})
export class ZaehlerApiService {
  private readonly api = inject(APIService);
  private readonly http = inject(HttpClient);
  private readonly pruefungsTyp = inject(PrüfungsTypStore);
  private readonly zaehlerDB = inject(ZaehlerDB);
  //#region Verwaltung
  //#region Zählernames
  public getZaehlerNames(otypidFn: () => number, sourceTypFn: () => ZählerMode) {
    return httpResource<Table<ZählerName>>(() => `${ROUTE}/names/${otypidFn()}/${sourceTypFn()}`, {
      defaultValue: TABLE_EMPTY(),
    });
  }

  public setZaehlerName(otypid: number, sourceTyp: ZählerMode, sendObj: ZählerName) {
    return firstValueFrom(this.http.post<MRproResponseInfo<ID>>(`${ROUTE}/name/${otypid}/${sourceTyp}`, sendObj));
  }

  public copyZaehlerName(id: number, sendObj: { CopyText: string }) {
    return firstValueFrom(this.http.post<number>(`${ROUTE}/name/copy/${id}`, sendObj));
  }

  //#region Skizzen Points
  public getSkizzenPoints(name: string) {
    return firstValueFrom(this.http.get<Table<SkizzenPoint>>(`${ROUTE}/skizze/points/${name}`));
  }

  public setSkizzenPoint(name: string, sendObj: SkizzenPoint) {
    return firstValueFrom(this.http.post(`${ROUTE}/skizze/points/${name}`, sendObj));
  }

  public deleteSkizzenPoint(id: number) {
    return firstValueFrom(this.http.delete(`${ROUTE}/skizze/point/${id}`));
  }

  public getSkizzeWithAusfallPoints(opruefid: number, filename: string) {
    return this.api.isRep ?
        this.api.anlagenDB.GetSkizzeByName(filename)
      : firstValueFrom(this.http.get<string>(`${ROUTE}/skizze/messung-points/${opruefid}/${filename}`));
  }
  //#endregion
  //#endregion
  //#region Zählerwerte
  public getZaehlerWerteBySZAEHNID(szaehnidFn: () => number) {
    return resource({
      params: szaehnidFn,
      loader: ({ params: szaehnid }) =>
        this.api.isRep ?
          this.zaehlerDB.GetZaehlerWerte(szaehnid)
        : firstValueFrom(this.http.get<Table<ZählerWert>>(`${ROUTE}/werte/${szaehnid}`)),
      defaultValue: TABLE_EMPTY(),
    });
  }

  public setZaehlerWert(szaehnid: number, sendObj: ZählerWert) {
    return firstValueFrom(this.http.post<MRproResponseInfo<ID>>(`${ROUTE}/wert/${szaehnid}`, sendObj));
  }

  public resetTrackInZaehlerWerten(szaehnid: number, track: number) {
    return firstValueFrom(this.http.patch<MRproResponse>(`${ROUTE}/wert/${szaehnid}/reset-track/${track}`, null));
  }

  public copyZaehlerWert(szaehnid: number, sendObj: { IDs: number[]; CopyText: string }) {
    return firstValueFrom(this.http.post<MRproResponse>(`${ROUTE}/wert/${szaehnid}/copy`, sendObj));
  }

  public resortZaehlerWerte(ids: number[]) {
    return firstValueFrom(this.http.post<MRproResponse>(`${ROUTE}/wert/resort`, ids));
  }
  //#endregion
  //#region Zählereinheiten
  public getZaehlerWerteEinheiten(otypidFn: () => number) {
    return httpResource<Table<ZählerEinheit>>(() => `${ROUTE}/einheiten/${otypidFn()}`, {
      defaultValue: TABLE_EMPTY(),
    });
  }

  public setZaehlerWerteEinheit(otypid: number, sendObj: ZählerEinheit) {
    return firstValueFrom(this.http.post<MRproResponseInfo<ID>>(`${ROUTE}/einheit/${otypid}`, sendObj));
  }
  //#endregion
  //#region Shared
  public deleteZahlerItem(mode: 'name' | 'wert' | 'meh', id: number) {
    return firstValueFrom(this.http.delete<MRproResponse>(`${ROUTE}/${mode}/${id}`));
  }

  public getToleranzenBezeichnungen() {
    const tolRs =
      this.api.isRep ?
        resource({ loader: () => this.zaehlerDB.GetToleranzenEinstellungen() })
      : httpResource<ToleranzenBezeichnungen>(() => `${ROUTE}/toleranzen`);
    return computed(() => tolRs.value() ?? defaultTolBez);
  }
  //#endregion
  //#endregion
  //#region Export/Import
  public exportZaehler(otypid: number, type: number, IDs: number[]) {
    return firstValueFrom(this.http.post(`${ROUTE}/export/${otypid}/${type}`, IDs, this.api.AUTH_HEADER_BIN));
  }

  public importZaehler(connectionId: string, otypid: number, sendObj: Record<string, Table>) {
    return firstValueFrom(this.http.post<MRproResponse>(`${ROUTE}/import/${otypid}/${connectionId}`, sendObj));
  }
  //#endregion
  //#region Erfassung
  public getZaehlerBySZAEHNID(szaehnidFn: () => number) {
    return resource({
      params: szaehnidFn,
      loader: ({ params: szaehnid }) =>
        this.api.isRep ?
          this.zaehlerDB.GetZaehlerBySZAEHNID(szaehnid)
        : firstValueFrom(this.http.get<ZählerName>(`${ROUTE}/name/id/${szaehnid}`)),
    });
  }

  public getZaehlerByMode(modeFn: () => VerknüpfungsTyp, objektFn: () => ZaehlerObjektInfo) {
    return resource({
      params: () => {
        const objekt = objektFn();
        return objekt ? { mode: modeFn(), objekt } : undefined;
      },
      loader: ({ params: { mode, objekt } }) => {
        switch (mode) {
          case VerknüpfungsTyp.Wartungszähler: {
            const mID = objekt.mID;
            return mID ? this.getZaehlerByMaskeID(mID) : Promise.resolve<ZählerName>(null);
          }
          case VerknüpfungsTyp.Hauptzähler:
            return this.getZaehlerByObjektID(objekt.OSTAMMID, this.pruefungsTyp.KPRUVID());
        }
      },
    });
  }

  public getZaehlerByObjektID(ostammid: number, kpruvid: number) {
    return this.api.isRep ?
        this.zaehlerDB.GetZaehlerByObjektID(ostammid, kpruvid)
      : firstValueFrom(this.http.get<ZählerName>(`${ROUTE}/name/objekt/${ostammid}/${kpruvid}`));
  }

  public getZaehlerByMaskeID(mID: number) {
    return this.api.isRep ?
        this.zaehlerDB.GetZaehlerByMaskeID(mID)
      : firstValueFrom(this.http.get<ZählerName>(`${ROUTE}/name/maske/${mID}`));
  }

  public getPruefungenByObjektID(ostammidFn: () => number) {
    return resource({
      params: ostammidFn,
      loader: ({ params: ostammid }) =>
        this.api.isRep ?
          this.zaehlerDB.GetPrüfungenByObjektID(ostammid)
        : firstValueFrom(this.http.get<ZählerPrüfung[]>(`${ROUTE}/pruefungen/${ostammid}`)),
    });
  }

  public getZaehlerWerteByPruefungID(opruefid: number, szaehnid: number) {
    return this.api.isRep ?
        this.zaehlerDB.GetZaehlerMessungByPruefungID(opruefid, szaehnid)
      : firstValueFrom(this.http.get<MesswertErfassung[]>(`${ROUTE}/messung/${opruefid}/${szaehnid}`));
  }

  public setZaehlerMessung(opruefid: number, szaehnid: number, sendObj: MessungModel) {
    return this.api.isRep ?
        from(this.zaehlerDB.SetZaehlerMessung(opruefid, szaehnid, sendObj))
      : this.http.post<MRproResponse>(`${ROUTE}/messung/${opruefid}/${szaehnid}`, sendObj);
  }

  public getZaehlerDropdownList(typ: MessungDropdownList) {
    return this.api.isRep ?
        from(this.zaehlerDB.GetZaehlerMessenderOrGerät(typ))
      : this.http.get<string[]>(`${ROUTE}/dropdown/${typ}`);
  }
  //#endregion
  //#region Übersicht
  public getZaehlerMessungByOPRUEFID(opruefid: number) {
    return this.api.isRep ?
        this.zaehlerDB.GetZaehlerMessungByOPRUEFID(opruefid)
      : firstValueFrom(this.http.get<MesswertEintrag[]>(`${ROUTE}/messung/${opruefid}`));
  }
  //#endregion
}
